/**
 * Formatierter Notiztext – Fett, Kursiv, Farben, Grössen und Aufzählungen.
 *
 * Übernommen aus der BSS-App, wo sich dieselbe Formatierung im Alltag bewährt
 * hat, und auf das beschränkt, was eine Notiz braucht: keine Erwähnungen,
 * keine Zuordnung an Personen.
 *
 * ## Der Grundsatz: Der Text bleibt Text
 *
 * Gespeichert wird die Formatierung nicht **im** Text, sondern als Feld
 * **daneben** (`bodyRich` neben `body`). Im Text selbst steht weiterhin reiner
 * Text, ohne `**` und ohne spitze Klammern: Suche, Vorschau, Startbildschirm
 * und eine noch nicht aktualisierte App lesen denselben Text wie eh und je.
 *
 * Das Formatfeld trägt eine JSON-Beschreibung desselben Textes, zerlegt in
 * **Blöcke** (Absätze oder Listenpunkte mit Ebene) und **Läufe** (Textstücke
 * mit Marken). Damit die beiden nie auseinanderlaufen, gilt eine einzige
 * Regel: Die Klartext-Projektion des Formatfelds muss Zeichen für Zeichen dem
 * gespeicherten Text entsprechen (`richDocFor`). Hat irgendwer – etwa eine
 * ältere Fassung der App – nur den Text geändert, passt die Projektion nicht
 * mehr, und die Notiz steht stillschweigend unformatiert da. Falsch
 * formatierter Text ist schlimmer als unformatierter.
 *
 * ## Zwei Projektionen
 *
 * - `plainTextOf` ist der **gespeicherte** Text: Listenpunkte tragen ihr
 *   Aufzählungszeichen („• …", eingerückt je Ebene), damit auch Suche und
 *   Vorschau die Liste als Liste zeigen.
 * - `editTextOf` ist der Text, wie er **im Editor** steht: ohne die
 *   Aufzählungszeichen, denn die zeichnet der Browser als Marker vor die
 *   Zeile. Auf diesem Text rechnen Cursor und Auswahl.
 *
 * ## Dateien im Text
 *
 * Ein Bild oder PDF steht als eigener Block zwischen den Absätzen – oben und
 * unten lässt sich weiterschreiben. Im gespeicherten Text erscheint es als
 * Zeile „📎 Dateiname": So findet die Suche es am Namen, und eine ältere App
 * zeigt wenigstens, dass hier etwas lag. Die Datei selbst liegt auf dem Server
 * (`note_files`), der Block trägt nur ihre Kennung, ihren Namen und ihren Typ.
 *
 * Das Modell steht im geteilten Paket, weil beide Seiten es brauchen: Die App
 * rechnet damit beim Schreiben, der Server prüft damit, ob ein Formatfeld
 * überhaupt zum Text passt, bevor er es ablegt (`normalizeRich`). Die
 * Darstellung – welche Klassen zu „Rot" gehören – bleibt in der App.
 */
export const MAX_LIST_LEVEL = 4;
/** Aufzählungszeichen je Ebene – so steht die Liste auch im reinen Text da. */
const LIST_MARKERS = ['•', '◦', '▪', '▫'];
/** So steht eine Datei im gespeicherten Text: „📎 Rechnung.pdf". */
export const FILE_MARKER = '📎';
/* ------------------------------------------------------------------ */
/* Palette und Grössen                                                 */
/* ------------------------------------------------------------------ */
/**
 * Eine feste, kleine Palette statt freier Farben.
 *
 * Gespeichert wird der **Name**, gezeichnet wird in der App ein heller und
 * ein dunkler Wert dazu: Ein sattes Rot auf weissem Grund wäre auf dunklem
 * Grund unleserlich. Freie Farbwerte könnten das nicht – und mehr als eine
 * Handvoll Farben braucht eine Notiz nicht.
 */
export const TEXT_COLOR_KEYS = [
    'red',
    'orange',
    'amber',
    'green',
    'blue',
    'violet',
    'pink',
    'gray',
];
export const TEXT_COLOR_LABELS = {
    red: 'Rot',
    orange: 'Orange',
    amber: 'Gelb',
    green: 'Grün',
    blue: 'Blau',
    violet: 'Violett',
    pink: 'Rosa',
    gray: 'Grau',
};
export const BG_COLOR_KEYS = ['yellow', 'green', 'blue', 'pink', 'orange', 'violet'];
export const BG_COLOR_LABELS = {
    yellow: 'Gelb',
    green: 'Grün',
    blue: 'Blau',
    pink: 'Rosa',
    orange: 'Orange',
    violet: 'Violett',
};
/**
 * Feste Grössenstufen statt freier Zahlen.
 *
 * Drei Stufen neben „Normal" genügen: eine zum Zurücknehmen und zwei zum
 * Hervorheben, etwa für eine Zwischenüberschrift. Eine freie Eingabe ergäbe
 * in jeder Notiz eine andere Typografie, und niemand fände zweimal dieselbe.
 */
export const TEXT_SIZE_KEYS = ['s', 'l', 'xl'];
export const TEXT_SIZE_LABELS = {
    s: 'Klein',
    l: 'Gross',
    xl: 'Sehr gross',
};
const TEXT_COLOR_SET = new Set(TEXT_COLOR_KEYS);
const BG_COLOR_SET = new Set(BG_COLOR_KEYS);
const TEXT_SIZE_SET = new Set(TEXT_SIZE_KEYS);
/* ------------------------------------------------------------------ */
/* Marken lesen und vergleichen                                        */
/* ------------------------------------------------------------------ */
/** Nur die Marken eines Laufs – ohne den Text, ohne Unbekanntes. */
export function marksOf(run) {
    const marks = {};
    if (run.b)
        marks.b = true;
    if (run.i)
        marks.i = true;
    if (run.u)
        marks.u = true;
    if (run.size)
        marks.size = run.size;
    if (run.color)
        marks.color = run.color;
    if (run.bg)
        marks.bg = run.bg;
    return marks;
}
export function sameMarks(a, b) {
    return ((a.b ?? false) === (b.b ?? false) &&
        (a.i ?? false) === (b.i ?? false) &&
        (a.u ?? false) === (b.u ?? false) &&
        (a.size ?? '') === (b.size ?? '') &&
        (a.color ?? '') === (b.color ?? '') &&
        (a.bg ?? '') === (b.bg ?? ''));
}
export function hasMarks(marks) {
    return Boolean(marks.b || marks.i || marks.u || marks.size || marks.color || marks.bg);
}
/**
 * Marken als `data-rt`-Attribut, z. B. `b;color:red`.
 *
 * Im Editor tragen die `<span>` ihre Marken doppelt: als Klassen fürs Auge
 * und als dieses Attribut für den Rückweg. Gelesen wird **nur** das Attribut
 * – Klassen sind Darstellung und könnten sich jederzeit ändern.
 */
export function encodeMarks(marks) {
    const parts = [];
    if (marks.b)
        parts.push('b');
    if (marks.i)
        parts.push('i');
    if (marks.u)
        parts.push('u');
    if (marks.size)
        parts.push(`size:${marks.size}`);
    if (marks.color)
        parts.push(`color:${marks.color}`);
    if (marks.bg)
        parts.push(`bg:${marks.bg}`);
    return parts.join(';');
}
export function decodeMarks(value) {
    const marks = {};
    for (const part of value.split(';')) {
        if (part === 'b')
            marks.b = true;
        else if (part === 'i')
            marks.i = true;
        else if (part === 'u')
            marks.u = true;
        else if (part.startsWith('size:'))
            marks.size = part.slice(5);
        else if (part.startsWith('color:'))
            marks.color = part.slice(6);
        else if (part.startsWith('bg:'))
            marks.bg = part.slice(3);
    }
    return marks;
}
/* ------------------------------------------------------------------ */
/* Aufbauen und projizieren                                            */
/* ------------------------------------------------------------------ */
function clampLevel(value) {
    return Math.min(MAX_LIST_LEVEL, Math.max(1, Math.round(value)));
}
/** Reiner Text als Modell – jede Zeile ein Absatz, nichts markiert. */
export function docFromPlain(text) {
    const lines = (text ?? '').split('\n');
    return { blocks: lines.map((line) => ({ runs: line ? [{ t: line }] : [] })) };
}
export function blockText(block) {
    return block.runs.map((run) => run.t).join('');
}
/** Der Text, wie er im Editor steht – ohne Aufzählungszeichen. */
export function editTextOf(doc) {
    return doc.blocks.map(blockText).join('\n');
}
/**
 * Der Text, wie er gespeichert wird – Listenpunkte mit Zeichen und Einzug.
 *
 * Ein leerer Listenpunkt endet ohne Leerzeichen („•", nicht „• "): Wer den
 * Text irgendwo an den Rändern beschneidet, soll damit nicht Text und
 * Formatfeld auseinanderbringen.
 */
export function plainTextOf(doc) {
    return doc.blocks
        .map((block) => {
        if (block.file)
            return `${FILE_MARKER} ${block.file.name}`;
        const text = blockText(block);
        if (!block.list)
            return text;
        const level = clampLevel(block.list);
        const marker = LIST_MARKERS[level - 1] ?? '•';
        return `${'  '.repeat(level - 1)}${marker}${text ? ` ${text}` : ''}`;
    })
        .join('\n');
}
export function editLengthOf(doc) {
    return doc.blocks.reduce((sum, block) => sum + blockText(block).length, 0) + doc.blocks.length - 1;
}
/**
 * Aufräumen: gleiche Nachbarläufe verschmelzen, Leeres verwerfen, Ebenen
 * begrenzen. Alles Weitere setzt einen aufgeräumten Stand voraus.
 */
export function normalizeDoc(doc) {
    const blocks = doc.blocks.map((block) => {
        // Ein Dateiblock trägt nur die Datei – keinen Text, keine Listenebene.
        if (block.file)
            return { runs: [], file: cleanFile(block.file) };
        const runs = [];
        for (const raw of block.runs) {
            const t = raw.t.replace(/\r/g, '').replace(/\n/g, ' ');
            if (!t)
                continue;
            const marks = marksOf(raw);
            const last = runs[runs.length - 1];
            if (last && sameMarks(last, marks))
                last.t += t;
            else
                runs.push({ t, ...marks });
        }
        const clean = { runs };
        if (block.list)
            clean.list = clampLevel(block.list);
        return clean;
    });
    return { blocks: blocks.length > 0 ? blocks : [{ runs: [] }] };
}
/** Trägt das Modell überhaupt eine Formatierung – oder eine Datei? */
export function isPlainDoc(doc) {
    return doc.blocks.every((block) => !block.file && !block.list && block.runs.every((run) => !hasMarks(run)));
}
/**
 * Der Name einer Datei, wie er im Text stehen darf: eine Zeile, ohne
 * Steuerzeichen, nicht endlos lang. Sonst bräche ein Zeilenumbruch im Namen
 * die Regel, dass jeder Block genau eine Zeile im gespeicherten Text ist.
 */
export function cleanFileName(name) {
    const flat = name
        .replace(/[\u0000-\u001f\u007f]+/g, ' ')
        .replace(/\s+/g, ' ')
        .trim()
        .slice(0, MAX_FILE_NAME)
        .trim();
    return flat || 'Datei';
}
const MAX_FILE_NAME = 200;
function cleanFile(file) {
    return {
        id: file.id,
        name: cleanFileName(file.name),
        mime: file.mime,
        size: Math.max(0, Math.round(file.size)),
    };
}
/** Ein Block, der eine Datei trägt. */
export function fileBlock(file) {
    return { runs: [], file: cleanFile(file) };
}
/** Die Kennungen aller Dateien im Text – für den Server, der sie der Notiz zuordnet. */
export function fileIdsOf(doc) {
    return doc.blocks.flatMap((block) => (block.file ? [block.file.id] : []));
}
/* ------------------------------------------------------------------ */
/* Speichern und Lesen                                                 */
/* ------------------------------------------------------------------ */
const FORMAT_VERSION = 1;
/**
 * Obergrenzen beim Einlesen – gegen kaputte oder bösartig grosse Felder.
 *
 * Grosszügig bemessen: Eine Notiz darf 20 000 Zeichen lang sein, und jeder
 * Block und jeder Lauf trägt mindestens eines davon. Was darüber liegt, kann
 * nicht zu einer gültigen Notiz gehören.
 */
const LIMITS = { blocks: 25_000, runs: 50_000 };
export function serializeDoc(doc) {
    const blocks = doc.blocks.map((block) => {
        if (block.file) {
            const { id, name, mime, size } = block.file;
            return { runs: [], file: { id, name, mime, size } };
        }
        const runs = block.runs.map((run) => {
            const out = { t: run.t };
            if (run.b)
                out.b = true;
            if (run.i)
                out.i = true;
            if (run.u)
                out.u = true;
            if (run.size)
                out.size = run.size;
            if (run.color)
                out.color = run.color;
            if (run.bg)
                out.bg = run.bg;
            return out;
        });
        return block.list ? { list: block.list, runs } : { runs };
    });
    return JSON.stringify({ v: FORMAT_VERSION, blocks });
}
/**
 * Das Formatfeld einlesen – wohlwollend im Kleinen, streng im Grossen.
 *
 * Eine unbekannte Farbe fällt weg (der Text bleibt), ein unbekannter Aufbau
 * lässt das ganze Feld fallen (`null`): Dann gilt der reine Text, und der ist
 * immer da.
 */
export function parseRichJson(json) {
    let raw;
    try {
        raw = JSON.parse(json);
    }
    catch {
        return null;
    }
    if (!raw || typeof raw !== 'object')
        return null;
    const { v, blocks: rawBlocks } = raw;
    if (v !== FORMAT_VERSION || !Array.isArray(rawBlocks) || rawBlocks.length > LIMITS.blocks) {
        return null;
    }
    let runCount = 0;
    const blocks = [];
    for (const entry of rawBlocks) {
        if (!entry || typeof entry !== 'object')
            return null;
        const { list, runs: rawRuns, file, } = entry;
        if (!Array.isArray(rawRuns))
            return null;
        if (file !== undefined) {
            // Eine Datei, die sich nicht eindeutig lesen lässt, verwirft das ganze
            // Feld: Ohne sie stimmte der Text nicht mehr, und dann gilt er allein.
            const parsed = parseFile(file);
            if (!parsed || rawRuns.length > 0)
                return null;
            blocks.push({ runs: [], file: parsed });
            continue;
        }
        const block = { runs: [] };
        if (typeof list === 'number' && Number.isFinite(list))
            block.list = clampLevel(list);
        for (const rawRun of rawRuns) {
            if (!rawRun || typeof rawRun !== 'object')
                return null;
            const { t, b, i, u, size, color, bg } = rawRun;
            if (typeof t !== 'string')
                return null;
            if (++runCount > LIMITS.runs)
                return null;
            const run = { t };
            if (b === true)
                run.b = true;
            if (i === true)
                run.i = true;
            if (u === true)
                run.u = true;
            if (typeof size === 'string' && TEXT_SIZE_SET.has(size))
                run.size = size;
            if (typeof color === 'string' && TEXT_COLOR_SET.has(color))
                run.color = color;
            if (typeof bg === 'string' && BG_COLOR_SET.has(bg))
                run.bg = bg;
            block.runs.push(run);
        }
        blocks.push(block);
    }
    return normalizeDoc({ blocks });
}
const FILE_ID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/;
function parseFile(raw) {
    if (!raw || typeof raw !== 'object')
        return null;
    const { id, name, mime, size } = raw;
    if (typeof id !== 'string' || !FILE_ID.test(id))
        return null;
    if (typeof name !== 'string' || typeof mime !== 'string' || mime.length > 100)
        return null;
    if (typeof size !== 'number' || !Number.isFinite(size) || size < 0)
        return null;
    return cleanFile({ id, name, mime, size });
}
/** Aus dem Modell das Paar machen, das gespeichert wird. */
export function toRichValue(doc) {
    const normal = normalizeDoc(doc);
    return {
        text: plainTextOf(normal),
        rich: isPlainDoc(normal) ? null : serializeDoc(normal),
    };
}
export function richValueOf(text, rich) {
    return { text: text ?? '', rich: rich ?? null };
}
/**
 * Das Formatfeld zu einem Text – oder `null`, wenn es nicht (mehr) passt.
 *
 * Der Wächter dieser ganzen Einrichtung: Nur wenn die Projektion des
 * Formatfelds Zeichen für Zeichen dem gespeicherten Text entspricht, wird
 * formatiert gezeichnet. Alles andere hiesse, einen Text zu zeigen, den so
 * niemand geschrieben hat.
 */
export function richDocFor(rich, text) {
    if (!rich)
        return null;
    const doc = parseRichJson(rich);
    if (!doc)
        return null;
    return plainTextOf(doc) === (text ?? '') ? doc : null;
}
/** Das Modell zu einem gespeicherten Paar – notfalls der reine Text. */
export function docOfValue(value) {
    return richDocFor(value.rich ?? null, value.text) ?? docFromPlain(value.text);
}
/**
 * Das Formatfeld, wie es abgelegt wird – aufgeräumt, oder gar keins.
 *
 * Für den Server: Er nimmt nur ein Formatfeld an, das zum Text passt, und
 * legt es in der aufgeräumten Form ab. Trägt es keine Formatierung (mehr),
 * bleibt das Feld leer – so sieht eine unformatierte Notiz immer gleich aus,
 * egal auf welchem Weg sie entstanden ist.
 */
export function normalizeRich(text, rich) {
    const doc = richDocFor(rich, text);
    if (!doc || isPlainDoc(doc))
        return null;
    return serializeDoc(doc);
}
function blockPositions(doc) {
    const positions = [];
    let offset = 0;
    for (const block of doc.blocks) {
        const length = blockText(block).length;
        positions.push({ start: offset, end: offset + length });
        offset += length + 1;
    }
    return positions;
}
function clampRange(doc, start, end) {
    const length = editLengthOf(doc);
    const from = Math.max(0, Math.min(start, length));
    const to = Math.max(0, Math.min(end, length));
    return from <= to ? [from, to] : [to, from];
}
/** Läufe an den angegebenen Stellen (lokal zum Block) auftrennen. */
function splitRuns(runs, cuts) {
    const points = [...new Set(cuts)].sort((a, b) => a - b);
    const out = [];
    let pos = 0;
    for (const run of runs) {
        const end = pos + run.t.length;
        let from = pos;
        for (const cut of points) {
            if (cut <= from || cut >= end)
                continue;
            out.push({ ...run, t: run.t.slice(from - pos, cut - pos) });
            from = cut;
        }
        out.push({ ...run, t: run.t.slice(from - pos) });
        pos = end;
    }
    return out.filter((run) => run.t !== '');
}
/** Die Läufe eines Blocks im lokalen Bereich [from, to). */
function runsSlice(runs, from, to) {
    const out = [];
    let pos = 0;
    for (const run of runs) {
        const end = pos + run.t.length;
        const cutFrom = Math.max(from, pos);
        const cutTo = Math.min(to, end);
        if (cutFrom < cutTo)
            out.push({ ...run, t: run.t.slice(cutFrom - pos, cutTo - pos) });
        pos = end;
    }
    return out;
}
function withMark(run, key, value) {
    const next = { t: run.t, ...marksOf(run) };
    if (value === null)
        delete next[key];
    else if (key === 'b' || key === 'i' || key === 'u')
        next[key] = true;
    else
        next[key] = value;
    return next;
}
/** Eine Marke über den Bereich setzen (`value`) oder entfernen (`null`). */
export function applyMark(doc, start, end, key, value) {
    const [from, to] = clampRange(doc, start, end);
    if (from === to)
        return doc;
    const positions = blockPositions(doc);
    const blocks = doc.blocks.map((block, index) => {
        const { start: bs, end: be } = positions[index];
        const localFrom = Math.max(from, bs) - bs;
        const localTo = Math.min(to, be) - bs;
        if (localFrom >= localTo)
            return block;
        const runs = splitRuns(block.runs, [localFrom, localTo]);
        let pos = 0;
        const next = runs.map((run) => {
            const runStart = pos;
            pos += run.t.length;
            if (runStart < localFrom || runStart >= localTo)
                return run;
            return withMark(run, key, value);
        });
        return { ...block, runs: next };
    });
    return normalizeDoc({ blocks });
}
/** Alle Marken im Bereich entfernen – die Listenform bleibt. */
export function clearMarks(doc, start, end) {
    const [from, to] = clampRange(doc, start, end);
    if (from === to)
        return doc;
    const positions = blockPositions(doc);
    const blocks = doc.blocks.map((block, index) => {
        const { start: bs, end: be } = positions[index];
        const localFrom = Math.max(from, bs) - bs;
        const localTo = Math.min(to, be) - bs;
        if (localFrom >= localTo)
            return block;
        const runs = splitRuns(block.runs, [localFrom, localTo]);
        let pos = 0;
        const next = runs.map((run) => {
            const runStart = pos;
            pos += run.t.length;
            if (runStart < localFrom || runStart >= localTo)
                return run;
            return { t: run.t };
        });
        return { ...block, runs: next };
    });
    return normalizeDoc({ blocks });
}
/** Der Lauf, in dem das Zeichen an `offset` steht – `null` auf einem `\n`. */
function runAtOffset(doc, offset) {
    const positions = blockPositions(doc);
    for (let index = 0; index < doc.blocks.length; index++) {
        const { start: bs, end: be } = positions[index];
        if (offset < bs || offset >= be)
            continue;
        let pos = bs;
        for (const run of doc.blocks[index].runs) {
            if (offset < pos + run.t.length)
                return run;
            pos += run.t.length;
        }
    }
    return null;
}
/**
 * Die Marken an der Auswahl – für den Zustand des Menüs.
 *
 * Massgeblich ist das erste markierte Zeichen; beim blossen Cursor das
 * Zeichen davor (so schreibt der Browser auch weiter) und am Zeilenanfang
 * das danach. Das genügt: Das Menü zeigt an, was ein Griff bewirken würde,
 * keine Statistik über die ganze Auswahl.
 */
export function marksAt(doc, start, end) {
    const [from, to] = clampRange(doc, start, end);
    const candidates = from < to ? [from, from - 1] : [from - 1, from];
    for (const candidate of candidates) {
        if (candidate < 0)
            continue;
        const run = runAtOffset(doc, candidate);
        if (run)
            return marksOf(run);
    }
    return {};
}
/** Erster und letzter Block, den der Bereich berührt (einschliesslich). */
function blockIndexRange(doc, start, end) {
    const [from, to] = clampRange(doc, start, end);
    const positions = blockPositions(doc);
    let first = 0;
    let last = doc.blocks.length - 1;
    for (let index = 0; index < positions.length; index++) {
        const position = positions[index];
        // Das `\n` hinter einem Block gehört noch zu ihm – ein Cursor am
        // Zeilenende meint diese Zeile.
        if (from >= position.start && from <= position.end)
            first = index;
        if (to >= position.start && to <= position.end) {
            last = index;
            break;
        }
    }
    return [first, Math.max(first, last)];
}
/** Der Listenstand an der Auswahl – für Menü und Tab-Taste. */
export function listStateAt(doc, start, end) {
    const [first] = blockIndexRange(doc, start, end);
    const block = doc.blocks[first];
    return block?.list ? { active: true, level: clampLevel(block.list) } : { active: false, level: 0 };
}
/**
 * Aufzählung ein- bzw. ausschalten – für alle Blöcke der Auswahl. Eine Datei
 * in der Auswahl bleibt, was sie ist: ein Block für sich.
 */
export function toggleList(doc, start, end) {
    const [first, last] = blockIndexRange(doc, start, end);
    const allList = doc.blocks
        .slice(first, last + 1)
        .filter((block) => !block.file)
        .every((block) => block.list);
    const blocks = doc.blocks.map((block, index) => {
        if (index < first || index > last || block.file)
            return block;
        if (allList)
            return { runs: block.runs };
        return { ...block, list: block.list ?? 1 };
    });
    return normalizeDoc({ blocks });
}
/**
 * Ein- oder ausrücken. Einrücken macht aus einem Absatz einen Listenpunkt;
 * Ausrücken auf Ebene 1 macht daraus wieder einen Absatz.
 */
export function changeIndent(doc, start, end, delta) {
    const [first, last] = blockIndexRange(doc, start, end);
    const blocks = doc.blocks.map((block, index) => {
        if (index < first || index > last || block.file)
            return block;
        if (delta === 1) {
            return { ...block, list: block.list ? Math.min(MAX_LIST_LEVEL, block.list + 1) : 1 };
        }
        if (!block.list)
            return block;
        if (block.list <= 1)
            return { runs: block.runs };
        return { ...block, list: block.list - 1 };
    });
    return normalizeDoc({ blocks });
}
/**
 * Einen Bereich durch Text ersetzen – für das Einfügen aus der Zwischenablage.
 *
 * Eingesetzter Text übernimmt die Marken des Zeichens davor: Wer mitten im
 * fett Geschriebenen etwas einfügt, bekommt es fett, wie weitergetippt.
 * Mehrzeiliges spaltet Blöcke; neue Zeilen erben die Listenebene der
 * Einfügestelle.
 *
 * Eine Datei am Rand des Bereichs bleibt stehen – der Text kommt unter bzw.
 * über sie. Nur was ganz innerhalb der Auswahl liegt, wird ersetzt.
 */
export function replaceRange(doc, start, end, text) {
    const [from, to] = clampRange(doc, start, end);
    const clean = text.replace(/\r\n?/g, '\n');
    const lines = clean.split('\n');
    const positions = blockPositions(doc);
    const [startIndex, endIndex] = blockIndexRange(doc, from, to);
    const rawStart = doc.blocks[startIndex];
    const rawEnd = doc.blocks[endIndex];
    const keepBefore = rawStart.file ? [rawStart] : [];
    const keepAfter = rawEnd.file && endIndex !== startIndex ? [rawEnd] : [];
    // Für die Rechnung steht an der Stelle einer Datei ein leerer Absatz.
    const startBlock = rawStart.file ? { runs: [] } : rawStart;
    const endBlock = endIndex === startIndex ? startBlock : rawEnd.file ? { runs: [] } : rawEnd;
    const startPos = positions[startIndex];
    const endPos = positions[endIndex];
    const localFrom = Math.min(from - startPos.start, blockText(startBlock).length);
    const localTo = Math.min(to - endPos.start, blockText(endBlock).length);
    const before = runsSlice(startBlock.runs, 0, localFrom);
    const after = runsSlice(endBlock.runs, localTo, Number.POSITIVE_INFINITY);
    const marks = localFrom > 0 ? marksOf(runsSlice(startBlock.runs, localFrom - 1, localFrom)[0] ?? {}) : {};
    const inserted = [];
    const firstRuns = [...before];
    if (lines[0])
        firstRuns.push({ t: lines[0], ...marks });
    const firstBlock = startBlock.list
        ? { list: startBlock.list, runs: firstRuns }
        : { runs: firstRuns };
    if (lines.length === 1) {
        firstBlock.runs = [...firstBlock.runs, ...after];
        inserted.push(firstBlock);
    }
    else {
        inserted.push(firstBlock);
        for (let index = 1; index < lines.length; index++) {
            const line = lines[index] ?? '';
            const runs = line ? [{ t: line, ...marks }] : [];
            const list = index === lines.length - 1 ? (endBlock.list ?? startBlock.list) : startBlock.list;
            inserted.push(list ? { list, runs } : { runs });
        }
        const lastBlock = inserted[inserted.length - 1];
        lastBlock.runs = [...lastBlock.runs, ...after];
    }
    const blocks = [
        ...doc.blocks.slice(0, startIndex),
        ...keepBefore,
        ...inserted,
        ...keepAfter,
        ...doc.blocks.slice(endIndex + 1),
    ];
    // Blieb eine Datei vorne stehen, beginnt der eingesetzte Text eine Zeile
    // tiefer – hinter ihrem Zeilenende.
    return { doc: normalizeDoc({ blocks }), caret: from + keepBefore.length + clean.length };
}
/**
 * Alles in einer Zeile: die Läufe aller Absätze hintereinander, getrennt durch
 * ein Leerzeichen, ohne Listenebene und ohne Dateien.
 *
 * Für Felder, die nur eine Zeile kennen – den Eintrag einer Checkliste. Ein
 * Zeilenumbruch, der dort trotzdem ankommt (eingefügter Text, ein Diktat),
 * soll die Wörter nicht aneinanderkleben, aber auch keine zweite Zeile
 * erzeugen, die es im gespeicherten Text gar nicht gäbe.
 */
export function singleLineBlock(doc) {
    const runs = [];
    for (const block of doc.blocks) {
        if (block.file || block.runs.length === 0)
            continue;
        if (runs.length > 0)
            runs.push({ t: ' ' });
        runs.push(...block.runs);
    }
    return normalizeDoc({ blocks: [{ runs }] }).blocks[0];
}
/**
 * Leerraum vorne und hinten weg – über die Läufe hinweg und nach derselben
 * Regel wie `String.prototype.trim`.
 *
 * Wer Text getrimmt speichert und die Formatierung daneben, muss beide gleich
 * trimmen: Sonst passte das Formatfeld um ein Leerzeichen nicht mehr zum Text,
 * und die Formatierung fiele stillschweigend weg.
 */
export function trimBlock(block) {
    const text = blockText(block);
    const from = text.length - text.trimStart().length;
    const to = text.trimEnd().length;
    if (from === 0 && to === text.length)
        return block;
    const runs = to > from ? runsSlice(block.runs, from, to) : [];
    return normalizeDoc({ blocks: [block.list ? { list: block.list, runs } : { runs }] })
        .blocks[0];
}
/** Text als Absätze – je Zeile einer, ohne Marken. */
export function blocksFromText(text) {
    return text
        .replace(/\r\n?/g, '\n')
        .split('\n')
        .map((line) => ({ runs: line ? [{ t: line }] : [] }));
}
/**
 * Blöcke an der Cursorstelle einfügen – Dateien oder erkannter Text.
 *
 * Der Absatz unter dem Cursor wird dort geteilt, wie es die Eingabetaste
 * täte: Was davor steht, bleibt oben, was danach steht, rückt unter die
 * eingefügten Blöcke. Oben und unten steht danach immer ein Absatz – auch ein
 * leerer –, damit sich über und unter einer Datei weiterschreiben lässt; eine
 * Datei ganz am Anfang oder Ende hätte sonst keine Zeile neben sich, in die
 * man tippen könnte.
 *
 * Zurück kommt die Stelle, an der es weitergeht: der Anfang des Absatzes
 * unter dem Eingefügten.
 */
export function insertBlocksAt(doc, caret, inserted) {
    if (inserted.length === 0)
        return { doc, caret };
    const normal = normalizeDoc(doc);
    const [from] = clampRange(normal, caret, caret);
    const positions = blockPositions(normal);
    const [index] = blockIndexRange(normal, from, from);
    const block = normal.blocks[index];
    const position = positions[index];
    let above;
    let below;
    if (block.file) {
        // Auf einer Datei: dahinter einfügen.
        above = [block];
        below = { runs: [] };
    }
    else {
        const local = Math.min(from - position.start, blockText(block).length);
        const head = runsSlice(block.runs, 0, local);
        const tail = runsSlice(block.runs, local, Number.POSITIVE_INFINITY);
        // Ein leerer Anfang braucht keine eigene Zeile, wenn darüber schon Text
        // steht – nur ganz oben oder unter einer Datei hält er den Platz frei.
        const previous = normal.blocks[index - 1];
        const headNeeded = head.length > 0 || !previous || Boolean(previous.file);
        above = headNeeded ? [block.list ? { list: block.list, runs: head } : { runs: head }] : [];
        // Ein leerer Rest wird ein gewöhnlicher Absatz: Ein leerer Listenpunkt
        // unter einem Bild sähe aus wie ein vergessener Strich.
        below = block.list && tail.length > 0 ? { list: block.list, runs: tail } : { runs: tail };
    }
    const blocks = [...normal.blocks.slice(0, index), ...above, ...inserted, below];
    const rest = normal.blocks.slice(index + 1);
    const result = normalizeDoc({ blocks: [...blocks, ...rest] });
    // Die Stelle, an der es weitergeht: der Anfang des Absatzes darunter.
    const belowIndex = index + above.length + inserted.length;
    const startOfBelow = blockPositions(result)[belowIndex]?.start ?? editLengthOf(result);
    return { doc: result, caret: startOfBelow };
}
/** Blöcke ans Ende anhängen – für Geteiltes, das zu einer bestehenden Notiz kommt. */
export function appendBlocks(doc, inserted) {
    return insertBlocksAt(doc, editLengthOf(normalizeDoc(doc)), inserted).doc;
}
/**
 * Dateien so hintereinander, dass zwischen je zweien eine leere Zeile steht.
 *
 * Wer mehrere Bilder auf einmal einfügt, will zu jedem etwas schreiben können.
 * Zwei Dateien direkt untereinander liessen keine Stelle dazwischen, an die
 * sich der Cursor setzen liesse – im Editor ist eine Datei ein Block, in den
 * man nicht hineintippt.
 */
export function fileBlocks(files) {
    return files.flatMap((file, index) => index === 0 ? [fileBlock(file)] : [{ runs: [] }, fileBlock(file)]);
}
function isEmptyParagraph(block) {
    return Boolean(block && !block.file && !block.list && block.runs.length === 0);
}
/**
 * Um jede Datei eine Zeile zum Schreiben: über einer Datei ganz oben, unter
 * einer Datei ganz unten und zwischen zwei Dateien je ein leerer Absatz.
 *
 * Das Einfügen sorgt schon selbst dafür (`insertBlocksAt`). Verloren gehen
 * kann eine solche Zeile trotzdem – wer im Editor die leere Zeile unter einem
 * Bild löscht, hätte danach keine mehr, in die er tippen könnte. Der Editor
 * zieht sie deshalb bei jedem Zeichnen nach. Steht schon alles da, kommt das
 * Modell unverändert zurück – dasselbe Objekt, damit sich billig prüfen lässt,
 * ob etwas zu tun war.
 */
export function withWritingLines(doc) {
    const blocks = [];
    for (const block of doc.blocks) {
        const previous = blocks[blocks.length - 1];
        if (block.file && (!previous || previous.file))
            blocks.push({ runs: [] });
        blocks.push(block);
    }
    if (blocks.length === 0 || blocks[blocks.length - 1]?.file)
        blocks.push({ runs: [] });
    return blocks.length === doc.blocks.length ? doc : { blocks };
}
/**
 * Stösst der Cursor an eine Datei – am Anfang einer Zeile, über der eine
 * steht (`-1`), am Ende einer Zeile, unter der eine steht (`1`), oder auf der
 * Zeile der Datei selbst?
 *
 * Der Editor fragt das vor Rücktaste und Entf. Der Browser löschte sonst die
 * ganze Datei mit einem einzigen Tastendruck: Wer unter einem Bild ein Wort zu
 * viel löscht, wäre das Bild los – ohne Rückfrage und ohne Weg zurück.
 * Entfernt wird eine Datei deshalb nur über ihr Kreuz.
 */
export function fileBeside(doc, caret, direction) {
    const positions = blockPositions(doc);
    for (let index = 0; index < doc.blocks.length; index++) {
        const { start, end } = positions[index];
        if (caret < start || caret > end)
            continue;
        if (doc.blocks[index]?.file)
            return true;
        return direction === -1
            ? caret === start && Boolean(doc.blocks[index - 1]?.file)
            : caret === end && Boolean(doc.blocks[index + 1]?.file);
    }
    return false;
}
/**
 * Eine Datei aus dem Text nehmen.
 *
 * Standen über und unter ihr leere Zeilen, bleibt davon eine – sonst hinterliesse
 * jedes Entfernen ein Loch. Zurück kommt die Stelle, an der es weitergeht: die
 * leere Zeile, die geblieben ist, oder der Anfang dessen, was nachrückt.
 */
export function removeFile(doc, id) {
    const normal = normalizeDoc(doc);
    const index = normal.blocks.findIndex((block) => block.file?.id === id);
    if (index === -1)
        return { doc: normal, caret: editLengthOf(normal) };
    const collapse = isEmptyParagraph(normal.blocks[index - 1]) && isEmptyParagraph(normal.blocks[index + 1]);
    const blocks = [
        ...normal.blocks.slice(0, index),
        ...normal.blocks.slice(index + (collapse ? 2 : 1)),
    ];
    const result = normalizeDoc({ blocks });
    const positions = blockPositions(result);
    const target = collapse ? index - 1 : index;
    const caret = positions[target]?.start ?? positions[positions.length - 1]?.end ?? editLengthOf(result);
    return { doc: result, caret };
}
/**
 * „- " am Zeilenanfang startet eine Aufzählung – wie man es aus anderen
 * Editoren im Griff hat.
 *
 * Geprüft wird nach jeder Eingabe: Steht der Cursor unmittelbar hinter einem
 * „- ", mit dem ein gewöhnlicher Absatz beginnt, wird der Absatz zum
 * Listenpunkt, und die beiden Zeichen verschwinden – sie waren Befehl, nicht
 * Text. Wer die Liste nicht wollte, schaltet sie im Formatmenü wieder aus.
 *
 * `null` heisst: kein Fall für die Verwandlung – die Eingabe bleibt, wie sie
 * ist.
 */
export function autoListBlock(doc, caret) {
    const positions = blockPositions(doc);
    for (let index = 0; index < doc.blocks.length; index++) {
        const block = doc.blocks[index];
        const { start, end } = positions[index];
        if (caret < start || caret > end)
            continue;
        if (block.list)
            return null;
        if (caret - start !== 2 || !blockText(block).startsWith('- '))
            return null;
        const runs = runsSlice(block.runs, 2, Number.POSITIVE_INFINITY);
        const blocks = doc.blocks.map((entry, at) => (at === index ? { list: 1, runs } : entry));
        return { doc: normalizeDoc({ blocks }), caret: start };
    }
    return null;
}
/**
 * Das Wort unter dem Cursor – die Fläche, auf die eine Marke ohne Auswahl
 * wirkt. Auf Weissraum gibt es keins, dann bewirkt der Griff nichts.
 */
export function wordRangeAt(text, position) {
    const isWord = (char) => char !== undefined && /[\p{L}\p{N}]/u.test(char);
    let start = Math.max(0, Math.min(position, text.length));
    let end = start;
    while (isWord(text[start - 1]))
        start--;
    while (isWord(text[end]))
        end++;
    return start === end ? null : { start, end };
}
//# sourceMappingURL=richtext.js.map